import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Box, Stack, Text, Draft } from '@resi-media/resi-ui';
import { useLocalStorage } from 'usehooks-ts';
import { useMockData } from '@preview/contexts/MockDataContext';
import { useRefresh } from '@preview/contexts/RefreshContext';
import type { YouTubeChannel, YouTubePlaylists, YouTubeVideos } from '@preview/types/youtube';

type PlaylistItemsData = {
  [playlistId: string]: YouTubeVideos.Response;
};

type YouTubeData = {
  channelData?: YouTubeChannel.Response;
  playlistItemsData?: PlaylistItemsData;
  playlistsData?: YouTubePlaylists.Response;
};

const CHANNEL_ID_REGEX = /^UC[A-Za-z0-9_-]{22}$/;

const YouTubeDataFetcherCard: React.FC = () => {
  const [channelInput, setChannelInput] = useLocalStorage('preview-youtube-channel-input', '');
  const [numPlaylists, setNumPlaylists] = useLocalStorage('preview-youtube-num-playlists', 2);
  const [numVideos, setNumVideos] = useLocalStorage('preview-youtube-num-videos', 2);
  const API_KEY = 'AIzaSyC6A_v2M0ozcySg3X9WuWo_k46MKsJlGO4';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { addPlaylistMock, addSiteMock, addVideoMock, clearAllMocks, embedId, resetPendingChanges } = useMockData();
  const { triggerRefresh } = useRefresh();

  const lookupChannelIdByQuery = async (query: Record<string, string>, errorLabel: string): Promise<string> => {
    const params = new URLSearchParams({ ...query, key: API_KEY, part: 'id' });
    const response = await fetch(`https://www.googleapis.com/youtube/v3/channels?${params.toString()}`);
    if (!response.ok) throw new Error(`${errorLabel} lookup failed: ${response.status}`);
    const data = await response.json();
    const id = data?.items?.[0]?.id;
    if (!id) throw new Error(`${errorLabel} not found`);
    return id;
  };

  const lookupChannelIdByVideo = async (videoId: string): Promise<string> => {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${encodeURIComponent(videoId)}&key=${API_KEY}`
    );
    if (!response.ok) throw new Error(`Video lookup failed: ${response.status}`);
    const data = await response.json();
    const id = data?.items?.[0]?.snippet?.channelId;
    if (!id) throw new Error(`Video ${videoId} not found`);
    return id;
  };

  const lookupChannelIdBySearch = async (q: string): Promise<string> => {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/search?part=snippet&type=channel&maxResults=1&q=${encodeURIComponent(q)}&key=${API_KEY}`
    );
    if (!response.ok) throw new Error(`Search lookup failed: ${response.status}`);
    const data = await response.json();
    const id = data?.items?.[0]?.snippet?.channelId;
    if (!id) throw new Error(`No channel found for "${q}"`);
    return id;
  };

  const resolveChannelId = async (raw: string): Promise<string> => {
    const trimmed = raw.trim();
    if (!trimmed) throw new Error('Channel ID, URL, or handle is required');

    if (CHANNEL_ID_REGEX.test(trimmed)) return trimmed;
    if (trimmed.startsWith('@')) return lookupChannelIdByQuery({ forHandle: trimmed.slice(1) }, `Handle ${trimmed}`);

    let url: URL | null = null;
    try {
      url = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    } catch {
      url = null;
    }

    if (!url || !/(^|\.)(youtube\.com|youtu\.be)$/i.test(url.hostname)) {
      return lookupChannelIdBySearch(trimmed);
    }

    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    const path = url.pathname;

    if (host === 'youtu.be') {
      const videoId = path.replace(/^\//, '').split('/')[0];
      if (videoId) return lookupChannelIdByVideo(videoId);
    }

    const channelMatch = path.match(/^\/channel\/(UC[A-Za-z0-9_-]{22})/);
    if (channelMatch) return channelMatch[1];

    const handleMatch = path.match(/^\/(@[A-Za-z0-9._-]+)/);
    if (handleMatch) return lookupChannelIdByQuery({ forHandle: handleMatch[1].slice(1) }, `Handle ${handleMatch[1]}`);

    const userMatch = path.match(/^\/user\/([A-Za-z0-9._-]+)/);
    if (userMatch) return lookupChannelIdByQuery({ forUsername: userMatch[1] }, `Username ${userMatch[1]}`);

    if (path === '/watch') {
      const v = url.searchParams.get('v');
      if (v) return lookupChannelIdByVideo(v);
    }

    const videoPathMatch = path.match(/^\/(?:shorts|embed|live|v)\/([A-Za-z0-9_-]+)/);
    if (videoPathMatch) return lookupChannelIdByVideo(videoPathMatch[1]);

    const customMatch = path.match(/^\/c\/([A-Za-z0-9._-]+)/);
    if (customMatch) return lookupChannelIdBySearch(customMatch[1]);

    throw new Error(`Could not resolve channel from: ${raw}`);
  };

  const fetchYouTubeData = async () => {
    if (!channelInput.trim()) {
      setError('Channel ID, URL, or handle is required');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const channelId = await resolveChannelId(channelInput);

      // 1. Fetch channel data
      const channelResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=snippet,contentDetails&id=${channelId}&key=${API_KEY}`
      );

      if (!channelResponse.ok) {
        throw new Error(`Channel API error: ${channelResponse.status}`);
      }

      const channelData: YouTubeChannel.Response = await channelResponse.json();

      if (channelData.items.length === 0) {
        throw new Error('Channel not found');
      }

      // 2. Fetch playlists data
      const playlistsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/playlists?part=snippet,contentDetails&channelId=${channelId}&key=${API_KEY}&maxResults=${numPlaylists}`
      );

      if (!playlistsResponse.ok) {
        throw new Error(`Playlists API error: ${playlistsResponse.status}`);
      }

      const playlistsData: YouTubePlaylists.Response = await playlistsResponse.json();

      // 3. Fetch playlist items (videos) for each playlist
      const playlistItemsData: PlaylistItemsData = {};

      for (const playlist of playlistsData.items) {
        const playlistId = playlist.id;
        const playlistItemsResponse = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${playlistId}&key=${API_KEY}&maxResults=${numVideos}`
        );

        if (!playlistItemsResponse.ok) {
          throw new Error(`Playlist items API error for playlist ${playlistId}: ${playlistItemsResponse.status}`);
        }

        const currentItemData: YouTubeVideos.Response = await playlistItemsResponse.json();

        playlistItemsData[playlistId] = currentItemData;
      }

      // Clear all existing mocks before transforming and creating new ones
      clearAllMocks();

      // Transform YouTube data into RESI mock data format
      transformAndCreateMocks({
        channelData,
        playlistsData,
        playlistItemsData,
      });

      const successMessage = `YouTube data successfully fetched and transformed into mocks. All existing mocks were cleared.`;
      setSuccess(successMessage);
      triggerRefresh();
      resetPendingChanges();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const transformAndCreateMocks = (youtubeData: YouTubeData) => {
    if (!youtubeData.channelData || !youtubeData.playlistsData || !youtubeData.playlistItemsData) {
      return;
    }

    const channelData = youtubeData.channelData.items[0];
    const channelId = channelData.id;
    const channelTitle = channelData.snippet.title;
    const channelDescription = channelData.snippet.description;

    // Create feed mock (site)
    const feedPlaylists = youtubeData.playlistsData.items.map((playlist) => ({
      id: playlist.id,
      name: playlist.snippet.title,
      size: playlist.contentDetails.itemCount,
      url: `/api/playlists/${playlist.id}`,
    }));

    const id = `site-yt-${channelId}`;

    addSiteMock(
      {
        id,
        description: `YouTube Channel: ${channelTitle}`,
        endpoint: '*sites-boot.resi.io/api/v1/sites?url=*',
        data: {
          id: `site-yt-${channelId}`,
          customerId: `customer-yt-${channelId}`,
          channelId: 'preview-channel-id',
          name: `${channelTitle} - YouTube Channel`,
          description: channelDescription || 'YouTube Channel Content',
          url: `https://youtube.com/channel/${channelId}`,
          branding: {
            mode: 'dark',
          },
          feeds: [
            {
              id: `feed-yt-${channelId}`,
              name: 'YouTube Feed',
              description: 'Content from YouTube channel',
              default: true,
              playlists: feedPlaylists,
            },
          ],
        },
      },
      true
    );

    // Create playlist mocks
    youtubeData.playlistsData.items.forEach((playlist) => {
      const playlistId = playlist.id;
      const playlistItems = youtubeData.playlistItemsData ? youtubeData.playlistItemsData[playlistId] : undefined;

      if (!playlistItems?.items) {
        return;
      }

      const videos = playlistItems.items.map((item) => {
        const videoId = item.snippet.resourceId.videoId;
        return {
          id: videoId,
          title: item.snippet.title,
          description: item.snippet.description,
          thumbnail: {
            url:
              item.snippet.thumbnails.maxres?.url ||
              item.snippet.thumbnails.standard?.url ||
              item.snippet.thumbnails.high?.url ||
              item.snippet.thumbnails.medium?.url ||
              item.snippet.thumbnails.default?.url,
          },
          airDate: item.snippet.publishedAt,
          embedId,
          formattedDescription: `<p>${item.snippet.description}</p>`,
        };
      });

      addPlaylistMock(
        {
          id: playlistId,
          endpoint: `*/api/playlists/${playlistId}*`,
          description: `YouTube Playlist: ${playlist.snippet.title}`,
          data: {
            id: playlistId,
            name: playlist.snippet.title,
            videos: videos,
          },
        },
        true
      );

      // Create individual video mocks
      videos.forEach((video) => {
        addVideoMock(
          {
            id: video.id,
            description: `YouTube Video: ${video.title}`,
            endpoint: `*/ondemand/videos/${video.id}`,
            data: {
              id: video.id,
              title: video.title,
              description: video.description,
              thumbnail: {
                url: video.thumbnail.url,
              },
              airDate: video.airDate,
              embedId,
              tags: ['youtube'],
              playlists: [{ id: playlistId, url: `*/api/playlists/${playlistId}*` }],
            },
          },
          true
        );
      });
    });
  };

  return (
    <Draft.Card>
      <Draft.CardSection>
        <Stack gap="m">
          <Text variant="h3">YouTube Data Fetcher</Text>
          <Text>Fetch data from a YouTube channel and transform it into mock data</Text>

          <Stack gap="s">
            {/* <Draft.FormField fieldLabel="YouTube API Key">
            <Draft.TextInput
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your YouTube API key"
              css={css`
                width: 100%;
              `}
            />
          </Draft.FormField> */}

            <Draft.FormField fieldLabel="Channel ID, URL, or @handle">
              <Draft.TextInput
                css={css`
                  width: 100%;
                `}
                onChange={(e) => setChannelInput(e.target.value)}
                placeholder="UCxxx, https://youtube.com/@handle, or a video URL"
                value={channelInput}
              />
            </Draft.FormField>
            <Text colorVariant="secondary" variant="body3">
              Accepts a channel ID (UC…), channel URL, video URL, @handle, /c/, or /user/ URL.
            </Text>

            <Box
              css={css`
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 16px;
              `}
            >
              <Draft.FormField fieldLabel="Max Playlists">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={50}
                  min={1}
                  onChange={(e) => setNumPlaylists(parseInt(e.target.value, 10) || 5)}
                  type="number"
                  value={numPlaylists.toString()}
                />
              </Draft.FormField>

              <Draft.FormField fieldLabel="Max Videos per Playlist">
                <Draft.TextInput
                  css={css`
                    width: 100%;
                  `}
                  max={50}
                  min={1}
                  onChange={(e) => setNumVideos(parseInt(e.target.value, 10) || 10)}
                  type="number"
                  value={numVideos.toString()}
                />
              </Draft.FormField>
            </Box>

            {error && <Text color="error">{error}</Text>}

            {success && <Text color="success">{success}</Text>}

            <Draft.Button
              css={css`
                margin-top: 8px;
              `}
              disabled={isLoading}
              label="Fetch YouTube Data"
              onClick={fetchYouTubeData}
            >
              {isLoading ? 'Loading...' : 'Fetch YouTube Data'}
            </Draft.Button>
          </Stack>
        </Stack>
      </Draft.CardSection>
    </Draft.Card>
  );
};

export default YouTubeDataFetcherCard;
